import { ElementRef, inject, Injectable, signal } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { BottomSheetConfig, BottomSheetLevel } from '../interfaces/mat.interface';
import { firstValueFrom } from 'rxjs';
import { FAST_SWIPE_VELOCITY_THRESHOLD, LEVEL_PROXIMITY_THRESHOLD, MIN_SWIPE_DISTANCE, MIN_SWIPE_TIME } from '../config/constans';

interface SwipeableElement extends HTMLElement {
  _swipeListeners?: {
    onClick: () => void;
    onTouchStart: (event: TouchEvent) => void;
    onTouchEnd: (event: TouchEvent) => void;
    onMouseDown: (event: MouseEvent) => void;
    onMouseUp: (event: MouseEvent) => void;
    onMouseMove: (event: MouseEvent) => void;
  };
}

@Injectable({
  providedIn: 'root'
})

export class MatBottomSheetService {
  private _bottomSheet = inject(MatBottomSheet);
  
  // Variables para tracking de velocidad
  private swipeStartTime = 0;
  private swipeStartY = 0;
  private wasFastSwipe = false;
  
  public containerHeightSignal = signal(null);

  private _bottomSheetCurrentLevel = signal<BottomSheetLevel>(BottomSheetLevel.EXPANDED);
  get bottomSheetCurrentLevel() {
    return this._bottomSheetCurrentLevel();
  }
  set bottomSheetCurrentLevel(level: BottomSheetLevel) {
    this._bottomSheetCurrentLevel.set(level);
  }
  
  private _containerHeight = signal<number>(window.innerHeight * 0.8);
  get containerHeight() {
    return this._containerHeight();
  }
  set containerHeight(height: number) {
    this._containerHeight.set(height);
  }
  
  private currentBottomSheetRef?: MatBottomSheetRef<unknown>;

  async openMatBottomSheet<T, D>(args: BottomSheetConfig<T, D>): Promise<MatBottomSheetRef<T>> {
    if (this.currentBottomSheetRef) {
      this.currentBottomSheetRef.dismiss();
      await firstValueFrom(this.currentBottomSheetRef.afterDismissed());
    }

    this._bottomSheetCurrentLevel.set(BottomSheetLevel.EXPANDED);

    const ref = this._bottomSheet.open<T, D>(args.component, {
      data: args.data ?? null,
      panelClass: args.panelClass ?? '',
    });

    this.currentBottomSheetRef = ref as MatBottomSheetRef<unknown>;

    ref.afterDismissed().subscribe(() => {
      this.currentBottomSheetRef = undefined;
      this._bottomSheetCurrentLevel.set(BottomSheetLevel.EXPANDED);
    });

    return ref;
  }

  closeMatBottomSheet<T>(matBottomSheetRef: MatBottomSheetRef<T>): void {
    matBottomSheetRef.dismiss();
    this._bottomSheetCurrentLevel.set(BottomSheetLevel.EXPANDED);
  }
  
  closeAllMatBottomSheets(): void {
    this._bottomSheet.dismiss();
    this._bottomSheetCurrentLevel.set(BottomSheetLevel.EXPANDED);
  }
  
  changeLevelBottomSheetOnSwipeDown(elementRef: ElementRef): void {
    const sheet = elementRef.nativeElement as SwipeableElement;
    const container = this._findClosestBottomSheetContainer(sheet);
    const childContent = this._findClosestBottomSheetChildContent(sheet);
    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const onTouchStart = (event: TouchEvent) => {
      isDragging = true;
      startY = event.touches[0].clientY;
      this.containerHeight = container.getBoundingClientRect().height;
      
      // Reset velocity tracking
      this.swipeStartTime = Date.now();
      this.swipeStartY = startY;
      this.wasFastSwipe = false;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!isDragging) return;
      
      // avoid default touch behavior (reloading page in mobile browsers)
      event.preventDefault();
      
      currentY = event.touches[0].clientY;
      const currentTime = Date.now();
      
      // Detectar si es un swipe rápido pero no cerrar aún
      const velocity = this._calculateOverallSwipeVelocity(currentY, currentTime);
      if (this._isFastSwipeDown(currentY, velocity, currentTime)) {
        this.wasFastSwipe = true;
      }
      
      this._handleSwipeMove(currentY, startY, container, this.containerHeight);
    };

    const onTouchEnd = () => {
      isDragging = false;
      
      // Primero verificar si está cerca de algún nivel específico
      if (this._isNearAnyLevel(container, childContent)) {
        this._handleSwipeEnd(container, childContent);
        return;
      }
      
      // Si no está cerca de ningún nivel y fue un swipe rápido, cerrar directamente
      if (this.wasFastSwipe) {
        this._handleFastSwipeClose();
        return;
      }
      
      // Comportamiento normal para otros casos
      this._handleSwipeEnd(container, childContent);
    };

    const onMouseDown = (event: MouseEvent) => {
      isDragging = true;
      startY = event.clientY;
      this.containerHeight = container.getBoundingClientRect().height;
      // console.log('initial Container Height:', this.containerHeight);
      
      // Reset velocity tracking
      this.swipeStartTime = Date.now();
      this.swipeStartY = startY;
      this.wasFastSwipe = false;

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (event: MouseEvent) => {
      const actualHeight = container.getBoundingClientRect()?.height;
      this.containerHeightSignal.set(actualHeight)

      if (!isDragging) return;
      currentY = event.clientY;
      const currentTime = Date.now();
      
      // Detectar si es un swipe rápido pero no cerrar aún
      const velocity = this._calculateOverallSwipeVelocity(currentY, currentTime);
      if (this._isFastSwipeDown(currentY, velocity, currentTime)) {
        this.wasFastSwipe = true;
      }

      this._handleSwipeMove(currentY, startY, container, this.containerHeight);
    };

    const onMouseUp = () => {
      isDragging = false;

      // Primero verificar si está cerca de algún nivel específico
      if (this._isNearAnyLevel(container, childContent)) {
        this._handleSwipeEnd(container, childContent);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        return;
      }

      // Si no está cerca de ningún nivel y fue un swipe rápido, cerrar directamente
      if (this.wasFastSwipe) {
        this._handleFastSwipeClose();
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        return;
      }

      // Comportamiento normal para otros casos
      this._handleSwipeEnd(container, childContent);

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    // add listeners to the sheet element
    sheet.addEventListener('touchstart', onTouchStart);
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd);
    sheet.addEventListener('mousedown', onMouseDown);

    // Guardar para remover luego
    sheet._swipeListeners = {
      onClick: () => this._bottomSheet.dismiss(),
      onTouchStart,
      onTouchEnd,
      onMouseDown,
      onMouseUp,
      onMouseMove
    };
  }

  private _handleSwipeMove = (
    currentY: number, 
    startY: number,
    container: HTMLElement,
    initialHeight: number
  ) => {
    const deltaY = currentY - startY;
    const newHeight = initialHeight - deltaY;

    const maxHeight = window.innerHeight * 0.8;
    const minHeight = 0;
    const clampedHeight = Math.max(Math.min(newHeight, maxHeight), minHeight);

    container.style.height = `${clampedHeight}px`;
    container.style.transform = 'translateY(0)';
  };

  private _handleSwipeEnd = (container: HTMLElement, childContent: HTMLElement) => {
    const viewportHeight = window.innerHeight;
    this.containerHeight = container.getBoundingClientRect().height;
    // const childContentHeight = childContent.getBoundingClientRect().height;
    // const maxContainerHeight = viewportHeight * 0.8;

    // console.log('Container height:', this.containerHeight);
    // console.log('Max container height:', maxContainerHeight);
    // console.log('childContent height:', childContentHeight);
    
    // const levelHeights: Record<BottomSheetLevel, number> = {
    //   [BottomSheetLevel.CLOSED]: 0,
    //   [BottomSheetLevel.MINIMIZED]: Math.min(childContentHeight * 0.1, window.innerHeight * 0.1),
    //   [BottomSheetLevel.MEDIUM]: Math.min(childContentHeight * 0.9, window.innerHeight * 0.9),
    //   [BottomSheetLevel.EXPANDED]: Math.min(childContentHeight, maxContainerHeight),
    // };

    const levelHeights: Record<BottomSheetLevel, number> = {
      [BottomSheetLevel.CLOSED]: 0,
      [BottomSheetLevel.MINIMIZED]: viewportHeight * 0.1,
      [BottomSheetLevel.MEDIUM]: viewportHeight * 0.4,
      [BottomSheetLevel.EXPANDED]: viewportHeight * 0.8,
    };

    const levels = Object.values(BottomSheetLevel).filter(v => !isNaN(Number(v))) as BottomSheetLevel[];

    const closestLevel = levels.reduce((closest, level) => {
      const diffCurrent = Math.abs(this.containerHeight - levelHeights[level]);
      const diffClosest = Math.abs(this.containerHeight - levelHeights[closest]);
      return diffCurrent < diffClosest ? level : closest;
    }, levels[0]);
    
    this._bottomSheetCurrentLevel.set(closestLevel);
    this._applyBottomSheetLevel(closestLevel, container, childContent);

    if (closestLevel === BottomSheetLevel.CLOSED) {
      this._bottomSheet.dismiss();
    }
  };

  private _applyBottomSheetLevel = (level: BottomSheetLevel, container: HTMLElement, childContent: HTMLElement) => {
    const childContentHeight = childContent.getBoundingClientRect().height;
    const startHeight = container.getBoundingClientRect().height;
    const duration = 300; 
    let targetHeight = 0;

    switch (level) {
      case BottomSheetLevel.MINIMIZED:
        this._bottomSheet.dismiss();
        break;
      case BottomSheetLevel.MEDIUM:
        targetHeight = childContentHeight; 
        break;
      case BottomSheetLevel.EXPANDED:
        targetHeight = childContentHeight;
        break;
    }

    const startTime = performance.now();
    const animate = (time: number) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      const currentHeight = startHeight + (targetHeight - startHeight) * eased;
      container.style.height = `${currentHeight}px`;
      this.containerHeightSignal.set(currentHeight);

      if (progress < 1) 
        requestAnimationFrame(animate);
      else 
        container.style.height = `${targetHeight}px`;
    };

    requestAnimationFrame(animate);
  };


  private _findClosestBottomSheetContainer(element: HTMLElement): HTMLElement | null {
    while (element && element !== document.body) {
      if (element.classList.contains('mat-bottom-sheet-container')) {
        return element;
      }
      element = element.parentElement!; // Use non-null assertion since we are traversing up the DOM
    }
    return null;
  }
  
  private _findClosestBottomSheetChildContent(element: HTMLElement): HTMLElement | null {
    while (element && element !== document.body) {
      if (element.classList.contains('mat-bottom-sheet-container')) {
        return element.firstElementChild as HTMLElement;
      }
      element = element.parentElement!; // Use non-null assertion since we are traversing up the DOM
    }
    return null;
  }

  private _calculateOverallSwipeVelocity(currentY: number, currentTime: number): number {
    const timeDiff = currentTime - this.swipeStartTime;
    
    if (timeDiff === 0) return 0;
    
    const distanceDiff = Math.abs(currentY - this.swipeStartY);
    const velocity = distanceDiff / timeDiff;
    
    return velocity;
  }

  private _isFastSwipeDown(currentY: number, velocity: number, currentTime: number): boolean {
    const swipeDistance = currentY - this.swipeStartY;
    const swipeTime = currentTime - this.swipeStartTime;
    
    const isSwipeDown = swipeDistance > 0;
    const hasMinDistance = Math.abs(swipeDistance) >= MIN_SWIPE_DISTANCE;
    const hasMinTime = swipeTime >= MIN_SWIPE_TIME;
    const isFastEnough = velocity >= FAST_SWIPE_VELOCITY_THRESHOLD;
    
    return isSwipeDown && hasMinDistance && hasMinTime && isFastEnough;
  }

  private _isNearAnyLevel(container: HTMLElement, childContent: HTMLElement): boolean {
    const viewportHeight = window.innerHeight;
    const currentHeight = container.getBoundingClientRect().height;
    const childContentHeight = childContent.getBoundingClientRect().height;
    const maxContainerHeight = viewportHeight * 0.8;
    
    const levelHeights: Record<BottomSheetLevel, number> = {
      [BottomSheetLevel.CLOSED]: 0,
      [BottomSheetLevel.MINIMIZED]: Math.min(childContentHeight * 0.1, window.innerHeight * 0.1),
      [BottomSheetLevel.MEDIUM]: Math.min(childContentHeight * 0.4, window.innerHeight * 0.4),
      [BottomSheetLevel.EXPANDED]: Math.min(childContentHeight, maxContainerHeight),
    };

    // Verificar si está cerca de algún nivel
    for (const level of Object.values(BottomSheetLevel).filter(v => !isNaN(Number(v))) as BottomSheetLevel[]) {
      const levelHeight = levelHeights[level];
      const distance = Math.abs(currentHeight - levelHeight);
      
      if (distance <= LEVEL_PROXIMITY_THRESHOLD) {
        // console.log(`📍 Near level ${BottomSheetLevel[level]} (distance: ${distance}px)`);
        return true;
      }
    }
    
    return false;
  }

  private _handleFastSwipeClose(): void {
    // console.log('🚀 Fast swipe detected - closing bottom sheet');
    this._bottomSheetCurrentLevel.set(BottomSheetLevel.CLOSED);
    this._bottomSheet.dismiss();
  }

  destroyBottomSheetOnSwipeDown(elementRef: ElementRef): void {
    const sheet = elementRef.nativeElement as SwipeableElement;
    const listeners = sheet._swipeListeners;
    if (!listeners) return;

    sheet.removeEventListener('touchstart', listeners.onTouchStart);
    sheet.removeEventListener('touchend', listeners.onTouchEnd);
    sheet.removeEventListener('mousedown', listeners.onMouseDown);
    window.removeEventListener('mousemove', listeners.onMouseMove);
    window.removeEventListener('mouseup', listeners.onMouseUp);
  }
}
